// 選択の動き（段階 C の2 の 7g、2026-10-26。前は d3 v4.10.2 の selection.transition()。d3-transition, Copyright 2010-2017 Mike Bostock, BSD-3-Clause の決まりに合わせた）。
// 決まり（止まったあとの形が前と同じになるように）:
// - 動きは作った次の画面の更新から。delay を過ぎたら始まり、同じ要素で先に始まっていた動きは止める（終わりの値は書かない）、先に作ってまだ始まっていない動きは取りやめる
// - attr・style に関数を渡したら、その場（.attr を呼んだとき）で要素ごとに値を出す。始めの値は動きが始まったときの属性（style は計算後の値）
// - 終わりは、その属性の補間の 1 のときの値（座標変換は "translate(x, y) scale(s,s)" のように書き直す・色は "rgb(r, g, b)"）。始めと終わりの文字が同じなら触らない
// - .transition() でつなげた動きは、前の動きの終わりから（時間と速さの変わり方は前の動きと同じ、delay は 0 から）
// 動きの間の値・時間の数え方は d3 と細かくは同じでなくてよい（2026-10-26 ユーザー）
import { easeCubicInOut, type Ease } from './ease.js';
import { interpolateForTransition, interpolateTransformSvg, type Interpolator } from './interpolate.js';
import { namespace, styleValue, type Selection } from './selection.js';
import { now, timeout, timer, type Timer } from './timer.js';

type Datum = unknown;
type Group = (Element | null)[];
type ValueFn<T> = (this: Element, d: Datum, i: number, group: Group) => T;
type Tween = (this: Element, d: Datum, i: number, group: Group) => ((t: number) => void) | null | undefined | void;

const CREATED = 0, SCHEDULED = 1, STARTING = 2, STARTED = 3, RUNNING = 4, ENDING = 5, ENDED = 6;

interface Schedule {
  name: string;
  index: number;
  group: Group;
  on: Record<string, ((this: Element, d: Datum, i: number, group: Group) => void)[]>;
  tween: { name: string; value: Tween }[];
  time: number;
  delay: number;
  duration: number;
  ease: Ease;
  timer: Timer | null;
  state: number;
  /** attr・style に渡した関数の、要素ごとの値（.attr を呼んだときに出す） */
  value?: Record<string, unknown>;
}
type Node = Element & { __data__?: Datum; __charatTr?: Record<number, Schedule> };

let lastId = 0;
const newId = (): number => ++lastId;

function getSchedule(node: Node, id: number): Schedule {
  const s = node.__charatTr && node.__charatTr[id];
  if (!s) throw new Error('transition not found');
  return s;
}
/** 始まる前だけ変えられる（delay・duration・ease・動かすもの） */
function setSchedule(node: Node, id: number): Schedule {
  const s = getSchedule(node, id);
  if (s.state > STARTING) throw new Error('too late; already running');
  return s;
}
function initSchedule(node: Node, id: number): Schedule {
  const s = getSchedule(node, id);
  if (s.state > CREATED) throw new Error('too late; already scheduled');
  return s;
}

function schedule(node: Node, name: string, id: number, index: number, group: Group, timing: { time: number; delay: number; duration: number; ease: Ease }): void {
  const schedules = node.__charatTr || (node.__charatTr = {});
  if (id in schedules) return;
  const self: Schedule = { name, index, group, on: {}, tween: [], time: timing.time, delay: timing.delay, duration: timing.duration, ease: timing.ease, timer: null, state: CREATED };
  schedules[id] = self;
  let tweens: ((t: number) => void)[] = [];
  const emit = (type: string): void => {
    const ls = self.on[type];
    if (ls) for (const l of ls.slice()) l.call(node, node.__data__, self.index, self.group);
  };
  const stop = (): void => {
    self.state = ENDED;
    self.timer!.stop();
    delete schedules[id];
    for (const _ in schedules) return;
    delete node.__charatTr;
  };
  const tick = (elapsed: number): void => {
    let t: number;
    if (elapsed < self.duration) t = self.ease(elapsed / self.duration);
    else {
      self.timer!.restart(stop);
      self.state = ENDING;
      t = 1;
    }
    for (const fn of tweens) fn(t);
    if (self.state === ENDING) {
      emit('end');
      stop();
    }
  };
  const start = (elapsed: number): void => {
    if (self.state !== SCHEDULED) return stop();
    for (const key in schedules) {
      const o = schedules[key];
      if (o.name !== self.name) continue;
      // ほかの動きが始まりかけなら、次に回す
      if (o.state === STARTED) {
        timeout(start);
        return;
      }
      // 動いている動きは止める（終わりの値は書かない）。先に作った動きは取りやめる
      if (o.state === RUNNING) {
        o.state = ENDED;
        o.timer!.stop();
        const ls = o.on.interrupt;
        if (ls) for (const l of ls.slice()) l.call(node, node.__data__, o.index, o.group);
        delete schedules[key];
      } else if (+key < id) {
        o.state = ENDED;
        o.timer!.stop();
        delete schedules[key];
      }
    }
    // 最初の一コマは同じ更新で
    timeout(() => {
      if (self.state === STARTED) {
        self.state = RUNNING;
        self.timer!.restart(tick, self.delay, self.time);
        tick(elapsed);
      }
    });
    self.state = STARTING;
    emit('start');
    if (self.state !== STARTING) return;
    self.state = STARTED;
    tweens = [];
    for (const tw of self.tween) {
      const fn = tw.value.call(node, node.__data__, self.index, self.group);
      if (fn) tweens.push(fn);
    }
  };
  self.timer = timer(
    (elapsed) => {
      self.state = SCHEDULED;
      self.timer!.restart(start, self.delay, self.time);
      if (self.delay <= elapsed) start(elapsed - self.delay);
    },
    0,
    self.time,
  );
}

export class Transition {
  constructor(
    public _groups: Group[],
    public _parents: unknown[],
    public _name: string,
    public _id: number,
  ) {}

  private eachNode(fn: (node: Node, i: number, group: Group) => void): this {
    for (const group of this._groups) for (let i = 0; i < group.length; i++) if (group[i]) fn(group[i] as Node, i, group);
    return this;
  }

  /** 始まるまでの時間（ms） */
  delay(value: number | ValueFn<number>): this {
    return this.eachNode((node, i, group) => {
      initSchedule(node, this._id).delay = typeof value === 'function' ? +value.call(node, node.__data__, i, group) : +value;
    });
  }
  /** 動く時間（ms） */
  duration(value: number | ValueFn<number>): this {
    return this.eachNode((node, i, group) => {
      setSchedule(node, this._id).duration = typeof value === 'function' ? +value.call(node, node.__data__, i, group) : +value;
    });
  }
  /** 速さの変わり方 */
  ease(value: Ease): this {
    if (typeof value !== 'function') throw new Error('ease');
    return this.eachNode((node) => {
      setSchedule(node, this._id).ease = value;
    });
  }
  /** 動きの始まり・終わり・止められたときに呼ぶ（"end" など。"end.名前" の名前は区別しない） */
  on(typename: string, listener: (this: Element, d: Datum, i: number, group: Group) => void): this {
    const types = typename.trim().split(/\s+/).map((t) => t.split('.')[0]);
    return this.eachNode((node) => {
      const s = setSchedule(node, this._id);
      for (const t of types) (s.on[t] || (s.on[t] = [])).push(listener);
    });
  }

  /** 動かすもの（同じ名前は差しかえる） */
  tween(name: string, value: Tween): this {
    return this.eachNode((node) => {
      const s = setSchedule(node, this._id);
      const tw = s.tween.slice();
      const k = tw.findIndex((t) => t.name === name);
      if (k >= 0) tw[k] = { name, value };
      else tw.push({ name, value });
      s.tween = tw;
    });
  }

  /** 要素ごとの値を、その場で出して覚える（attr・style に関数を渡したとき） */
  private tweenValue(key: string, value: ValueFn<unknown>): (node: Node) => unknown {
    const id = this._id;
    this.eachNode((node, i, group) => {
      const s = setSchedule(node, id);
      (s.value || (s.value = {}))[key] = value.call(node, node.__data__, i, group);
    });
    return (node) => getSchedule(node, id).value![key];
  }

  /** 属性を動かす（null なら始まったときに外す） */
  attr(name: string, value: unknown): this {
    const full = namespace(name);
    const interp = full === 'transform' ? (a: unknown, b: unknown) => interpolateTransformSvg(a as string | null, b as string | null) : interpolateForTransition;
    const get = (node: Element): string | null => (typeof full === 'string' ? node.getAttribute(full) : node.getAttributeNS(full.space, full.local));
    const set = (node: Element, v: unknown): void => {
      if (typeof full === 'string') node.setAttribute(full, String(v));
      else node.setAttributeNS(full.space, full.local, String(v));
    };
    const remove = (node: Element): void => {
      if (typeof full === 'string') node.removeAttribute(full);
      else node.removeAttributeNS(full.space, full.local);
    };
    let factory: (node: Element) => Interpolator | null | undefined;
    if (typeof value === 'function') {
      const v = this.tweenValue('attr.' + name, value as ValueFn<unknown>);
      factory = (node) => {
        const v1 = v(node as Node);
        if (v1 == null) return void remove(node);
        const v0 = get(node);
        return v0 === v1 ? null : interp(v0, v1);
      };
    } else if (value == null) {
      factory = (node) => void remove(node);
    } else {
      const v1 = value + '';
      factory = (node) => {
        const v0 = get(node);
        return v0 === v1 ? null : interp(v0, v1);
      };
    }
    return this.tween('attr.' + name, function (this: Element) {
      const node = this;
      const i = factory(node);
      return i ? (t: number) => set(node, i(t)) : null;
    });
  }

  /** 属性を、渡した補間で動かす（factory は始まったときに要素ごとに呼び、(t) => 値 を返す） */
  attrTween(name: string, factory: (this: Element, d: Datum, i: number, group: Group) => Interpolator | null | undefined): this {
    const full = namespace(name);
    return this.tween('attr.' + name, function (this: Element, d, i, group) {
      const node = this;
      const fn = factory.call(node, d, i, group);
      if (!fn) return null;
      return typeof full === 'string' ? (t: number) => node.setAttribute(full, String(fn(t))) : (t: number) => node.setAttributeNS(full.space, full.local, String(fn(t)));
    });
  }

  /** CSS を動かす（始めの値は計算後の値） */
  style(name: string, value: unknown, priority?: string): this {
    const pr = priority == null ? '' : priority;
    let factory: (node: Element) => Interpolator | null;
    if (value == null) {
      factory = (node) => {
        const v0 = styleValue(node, name);
        (node as HTMLElement).style.removeProperty(name);
        const v1 = styleValue(node, name);
        return v0 === v1 ? null : interpolateForTransition(v0, v1);
      };
      this.on('end', function (this: Element) {
        (this as HTMLElement).style.removeProperty(name);
      });
    } else if (typeof value === 'function') {
      const v = this.tweenValue('style.' + name, value as ValueFn<unknown>);
      factory = (node) => {
        const v0 = styleValue(node, name);
        let v1 = v(node as Node);
        if (v1 == null) {
          (node as HTMLElement).style.removeProperty(name);
          v1 = styleValue(node, name);
        }
        return v0 === v1 ? null : interpolateForTransition(v0, v1);
      };
    } else {
      const v1 = value + '';
      factory = (node) => {
        const v0 = styleValue(node, name);
        return v0 === v1 ? null : interpolateForTransition(v0, v1);
      };
    }
    return this.tween('style.' + name, function (this: Element) {
      const node = this as HTMLElement;
      const i = factory(node);
      return i ? (t: number) => node.style.setProperty(name, String(i(t)), pr) : null;
    });
  }

  /** つなげる（前の動きが終わってから。時間・速さの変わり方は同じ、delay は 0） */
  transition(): Transition {
    const id0 = this._id, id1 = newId();
    this.eachNode((node, i, group) => {
      const inherit = getSchedule(node, id0);
      schedule(node, this._name, id1, i, group, { time: inherit.time + inherit.delay + inherit.duration, delay: 0, duration: inherit.duration, ease: inherit.ease });
    });
    return new Transition(this._groups, this._parents, this._name, id1);
  }

  node(): Element | null {
    for (const group of this._groups) for (const n of group) if (n) return n;
    return null;
  }
}

/** 選択から動きを作る（d3 の selection.transition()。既定は 250ms・easeCubicInOut） */
export function transitionOf(sel: Selection, name?: string): Transition {
  const id = newId();
  const timing = { time: now(), delay: 0, duration: 250, ease: easeCubicInOut };
  const nm = name == null ? '' : name + '';
  for (const group of sel._groups) for (let i = 0; i < group.length; i++) if (group[i]) schedule(group[i] as Node, nm, id, i, group, timing);
  return new Transition(sel._groups, sel._parents, nm, id);
}
